import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Image as ImageIcon, X, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ImageUpload = ({ value, onChange, label, recommendedSize, testId }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max 5MB allowed.');
      return;
    }

    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPEG, PNG, WEBP, or GIF images allowed.');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await axios.post(`${API_URL}/api/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      onChange(data.url);
      toast.success('Image uploaded successfully!');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Upload failed';
      toast.error(typeof msg === 'string' ? msg : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={testId || 'image-upload'}>
      {label && (
        <label className="block text-sm font-body font-medium text-charcoal-700">{label}</label>
      )}
      {recommendedSize && (
        <p className="text-xs text-charcoal-500 italic">
          Recommended: {recommendedSize} | Max 5MB | JPEG, PNG, WEBP, GIF
        </p>
      )}
      <div className="flex gap-3">
        <div className="flex-1">
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Image URL or upload below"
            className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
          />
        </div>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 bg-saffron-600 text-white rounded-lg px-4 py-3 hover:bg-saffron-500 transition-colors disabled:opacity-50 text-sm font-medium whitespace-nowrap"
          data-testid={`${testId}-upload-button`}
        >
          {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
          {uploading ? 'Uploading...' : 'Upload'}
        </button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={(e) => handleUpload(e.target.files?.[0])}
        className="hidden"
      />
      {value && (
        <div className="relative inline-block mt-2">
          <img
            src={value}
            alt="Preview"
            className="max-h-40 rounded-lg border border-charcoal-200"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-700 transition-colors"
            aria-label="Remove image"
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
};
